import React, { Component } from "react";
import { compose } from "redux";
import * as constants from "../constants";
import withOrientation from "./withOrientation";
import { shouldLogPermissionGrantTime } from "../helpers/googleAnalyticsHelpers";
import { NoMatchOverlay } from "./NoMatchOverlay";
import { LiveSignals } from "./LiveSignals";
import { acquireCameraStream } from "../helpers/cameraStream";
import { trackOnce } from "../helpers/analytics";

const DISABLE_ZOOM = "DISABLE_ZOOM";
const ENABLE_ZOOM = "ENABLE_ZOOM";

/**
 * Extract a human-readable reason from whatever ImageCapture.grabFrame()
 * decided to reject with. Some browsers reject with a DOMException (has
 * .name like "InvalidStateError"), some with a generic Error (.message),
 * and some with bare undefined/null. Walk every option before giving up.
 */
const describeError = (err: unknown): string => {
  if (err == null) return "null/undefined rejection";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as any;
    if (anyErr.name && anyErr.message) return `${anyErr.name}: ${anyErr.message}`;
    if (anyErr.name) return anyErr.name;
    if (anyErr.message) return anyErr.message;
    if (anyErr.constructor?.name) return anyErr.constructor.name;
    try {
      return JSON.stringify(err).slice(0, 80);
    } catch {
      return "unstringifiable object";
    }
  }
  return String(err);
};

type CameraProps = {
  sessionYieldedMatch: boolean;
  beginScanning: () => void;
  snapAttempts: number;
  shouldBeScanning: boolean;
  processImageCapture: (imageBlob: any, captureRes?: string) => Promise<void>;
  onCaptureFailure?: (reason: string) => void;
};

type CameraState = {
  videoStream: MediaStream;
  frontCamera: boolean;
  snapAttempts: CameraProps["snapAttempts"];
  error?: string;
  // Mirrored video ref so LiveSignals (a child component using hooks) can
  // observe the element reactively. Refs don't trigger re-renders, so we
  // need state to plumb the element down once it's attached.
  videoEl: HTMLVideoElement | null;
};

export class CameraComponent extends Component<CameraProps, CameraState> {
  cropRectangle: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  scan: NodeJS.Timer;
  captureCounter: number;
  previewCanvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  vpreview: HTMLCanvasElement;
  initVideo: NodeJS.Timeout;
  drawPreview: NodeJS.Timeout;
  tempCanvas: HTMLCanvasElement;
  tempCtx: CanvasRenderingContext2D;
  // True between the start of a capture and its blob being submitted. If
  // the 333ms interval fires while we're still mid-capture, we skip rather
  // than start a second concurrent grabFrame() on the same track (which
  // collides with InvalidStateError and burns one of our 9 attempts).
  captureInFlight: boolean;
  // Mute-event diagnostics. Per the W3C Media Capture spec, mobile browsers
  // can auto-mute a video track under thermal stress, focus loss, etc.
  // readyState stays "live" but no frames flow — surfaces in our overlay
  // as silent freezes. These events tell us exactly when that's happening.
  muteStartTime: number;
  // performance.now() of the last successful drawVideoPreview run. If
  // drawImage(video) is being called regularly during freezes, this stays
  // small (<50ms). If our RAF loop is being throttled, this spikes.
  // Distinguishes "our loop stopped" from "drawImage drew stale frame".
  drawPreviewLastRanAt: number;

  constructor(props) {
    super(props);

    // Set state variables
    this.state = {
      videoStream: null,
      frontCamera: false,
      snapAttempts: this.props.snapAttempts,
      videoEl: null,
    };
  }

  setVideoRef = (el: HTMLVideoElement | null) => {
    this.video = el;
    if (el && this.state.videoEl !== el) {
      this.setState({ videoEl: el });
    }
  };

  resetSnapCounter = () => {
    let lastSnapTimestamp = parseInt(
      localStorage.getItem(constants.SNAP_LAST_TIMESTAMP)
    );

    if (lastSnapTimestamp) {
      let ttl =
        lastSnapTimestamp + constants.SNAP_COUNT_RESET_INTERVAL - Date.now();

      if (ttl <= 0 && this.state.snapAttempts > 0) {
        localStorage.removeItem(constants.SNAP_ATTEMPTS);
        this.setState({ snapAttempts: 0 });
      }
    }
  };

  /**
   * Captures a single scan directly from the camera track at native resolution.
   *
   * Note: this intentionally sends the FULL sensor frame, not the cropped
   * region shown in the preview rectangle. The resolver matches against
   * multi-scale center crops (full / 50% / 33% / 25%) server-side, so sending
   * the entire frame gives the engine the most signal to work with. The
   * preview rectangle is an aiming aid, not a hard crop region.
   */
  captureSingleScan = async () => {
    // Skip if a previous capture is still in flight. Prevents stacking
    // POSTs; doesn't increment captureCounter so the skipped tick doesn't
    // burn one of our 9 attempts.
    if (this.captureInFlight) {
      return null;
    }
    this.captureCounter += 1;

    if (this.captureCounter < 10) {
      if (!this.state.videoStream) return null;

      this.captureInFlight = true;
      try {
        // ROOT-CAUSE FIX for the Chromium video-freeze: we used to call
        // ImageCapture.grabFrame() here. On Chromium (Android specifically)
        // that method holds a strong reference to a slot in the
        // VideoCaptureBufferPool (3-4 slots total) for as long as the
        // returned ImageBitmap exists AND until the next frame arrives.
        // While that buffer is pinned, WebMediaPlayerMSCompositor cannot
        // advance its 'current frame' — so VideoFrameSubmitter stops
        // calling UpdateCurrentFrame(), rVFC stops firing, and the
        // <video> element shows the last presented frame for the full
        // duration grabFrame held the buffer.
        //
        // After PR #137 made us await processImageCapture, that pin
        // duration matched the full POST RTT (~500ms-2s). That's the
        // freeze. Firefox is immune because Gecko doesn't ship native
        // ImageCapture; the OLD codebase was immune because Vuforia
        // didn't call grabFrame periodically.
        //
        // drawImage(video) samples the currently-presented frame without
        // claiming a buffer pool slot — no contention with the compositor.
        // FOC-114 RETICLE-CROP variant: POST only the reticle region + a
        // margin, cropped 1:1 from the native (higher-res) source — NO upscale.
        // Per FocusEngine: the DINOv3 retrieval path center-crops the query to a
        // 256px square, so the win is making the WORK fill the frame — the 256px
        // resize budget then lands on the work (~2-5x) instead of the wall. The
        // reticle is where the visitor centres the work, so this follows the
        // work wherever it hangs (incl. high) and keeps it centred, surviving
        // the engine's centre-square. Real captured detail, not interpolation.
        // Full-frame baseline: drawImage(video, 0, 0, videoW, videoH).
        const videoW = this.video.videoWidth || 1080;
        const videoH = this.video.videoHeight || 1080;
        // Map the reticle box (cropRectangle, in viewport px) -> source-pixel
        // coords, mirroring object-fit:cover (same mapping as drawVideoPreview).
        const rect = this.video.getBoundingClientRect();
        const scale = Math.max(rect.width / videoW, rect.height / videoH);
        const offX = (rect.width - videoW * scale) / 2;
        const offY = (rect.height - videoH * scale) / 2;
        let sx = (this.cropRectangle.x - offX) / scale;
        let sy = (this.cropRectangle.y - offY) / scale;
        let sw = this.cropRectangle.width / scale;
        let sh = this.cropRectangle.height / scale;
        // Expand by a 20% margin per side (tolerate aim error + a work spilling
        // just past the reticle), clamped to the frame. Aspect-aware: we keep
        // the reticle's aspect rather than forcing a square (a forced square
        // clips wide works).
        const MARGIN = 0.2;
        const mx = sw * MARGIN;
        const my = sh * MARGIN;
        sx = Math.max(0, sx - mx);
        sy = Math.max(0, sy - my);
        sw = Math.min(videoW - sx, sw + 2 * mx);
        sh = Math.min(videoH - sy, sh + 2 * my);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(sw);
        canvas.height = Math.round(sh);
        const ctx = canvas.getContext("2d", { alpha: false });
        // 1:1 crop — dest size equals the source rect, so NO scaling/upscale.
        ctx.drawImage(this.video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        const imageBlob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
        });
        const captureRes = `${canvas.width}x${canvas.height} (reticle-crop)`;

        // AWAITED on purpose. Keeps captureInFlight=true until the POST
        // resolves, so the next setInterval tick won't fire another
        // capture+POST while one is still pending. Without the await,
        // captureInFlight cleared after the synchronous draw (~5ms) and
        // the next 333ms tick started a second POST, stacking up to 9
        // concurrent fetches and saturating the engine.
        await this.props.processImageCapture(imageBlob, captureRes);
      } catch (err) {
        // Extremely rare — usually means video element has no dimensions.
        console.error("Capture error:", err);
        if (this.props.onCaptureFailure) {
          this.props.onCaptureFailure(describeError(err));
        }
      } finally {
        this.captureInFlight = false;
      }
    }
  };

  playVideo = async () => {
    if (this.video) {
      try {
        await this.video.play();
        // Begin drawing the preview. drawVideoPreview no-ops if the video
        // doesn't have dimensions yet, so safe to call before frames arrive.
        requestAnimationFrame(this.drawVideoPreview);
      } catch (error) {
        console.log("Cannot play video stream", error);
      }
    }
  };

  async componentDidMount() {
    // Disable zoom
    this.pinchZoomModifier(DISABLE_ZOOM);

    const previewBox = this.vpreview.getBoundingClientRect();
    const { x, y, width, height } = previewBox;
    this.cropRectangle = {
      x: Math.floor(x),
      y: Math.floor(y),
      width: Math.floor(width),
      height: Math.floor(height),
    };

    // Fetch the device camera
    try {
      const startTime = Date.now();
      // Reuse the SHARED camera stream warmed by Home (helpers/cameraStream),
      // or acquire it here if we landed on /scan directly (hard refresh / deep
      // link). Reusing the already-open stream means no cold-acquire pause and
      // no blurred-background flash on scan -> artwork -> scan, and guarantees a
      // single stream (never the two-open contention of FOC-123). The scanner
      // constraints (environment, 1920² ideal, >=24fps) live in the manager.
      const videoStream = await acquireCameraStream();

      // Log the permission grant time if it took more than 900 ms
      // TODO - fix this
      // shouldLogPermissionGrantTime(startTime);

      this.setState({ videoStream }, () => {
        // When video is able to be captured
        if (this.state.videoStream) {
          // Wire up mute-event diagnostics on the track. The track stays
          // "live" while muted; we'd otherwise have no signal that the
          // pipeline stalled at the OS layer.
          const track = videoStream.getVideoTracks()[0];
          if (track) {
            track.addEventListener("mute", this.handleTrackMute);
            track.addEventListener("unmute", this.handleTrackUnmute);
          }

          // Wire up VIDEO ELEMENT events that signal pipeline state. The
          // browser fires these when the video stops playing because data
          // isn't available (`waiting`), when fetch fails (`stalled`),
          // when browser intentionally pauses (`suspend`), and when
          // playback resumes (`playing`). Whichever fires during a freeze
          // names the actual mechanism.
          if (this.video) {
            this.video.addEventListener("waiting", this.handleVideoWaiting);
            this.video.addEventListener("stalled", this.handleVideoStalled);
            this.video.addEventListener("suspend", this.handleVideoSuspend);
            this.video.addEventListener("playing", this.handleVideoPlayingEvent);
            this.video.addEventListener("pause", this.handleVideoPauseEvent);
            this.video.addEventListener("ratechange", this.handleVideoRateChange);
          }

          this.setupForCapturing();
          this.resetSnapCounter();
        }
      });
    } catch (error) {
      this.setState({ error: "An error occurred accessing the device camera" });
    }
  }

  handleTrackMute = () => {
    this.muteStartTime = Date.now();
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure("[TRACK MUTED — OS stalled the camera pipeline]");
    }
  };

  handleTrackUnmute = () => {
    const dur = this.muteStartTime ? Date.now() - this.muteStartTime : 0;
    this.muteStartTime = 0;
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure(`[TRACK UNMUTED — was muted ${dur}ms]`);
    }
  };

  // Video element pipeline events. These are the BROWSER telling us
  // directly when/why the video is stalling. Logged to debug overlay
  // AND console (so they show up in remote DevTools too).
  private waitingStartTime = 0;
  handleVideoWaiting = () => {
    this.waitingStartTime = Date.now();
    console.log(`[Camera] video 'waiting' at ${Date.now()} — playback stopped, next frame not ready`);
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure("[VIDEO WAITING — next frame not ready]");
    }
  };
  handleVideoPlayingEvent = () => {
    const dur = this.waitingStartTime ? Date.now() - this.waitingStartTime : 0;
    console.log(`[Camera] video 'playing' at ${Date.now()}${dur ? ` — was waiting ${dur}ms` : ""}`);
    if (dur && this.props.onCaptureFailure) {
      this.props.onCaptureFailure(`[VIDEO RESUMED — was waiting ${dur}ms]`);
    }
    this.waitingStartTime = 0;
  };
  handleVideoStalled = () => {
    console.log(`[Camera] video 'stalled' at ${Date.now()} — data fetch failed`);
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure("[VIDEO STALLED — data fetch failed]");
    }
  };
  handleVideoSuspend = () => {
    console.log(`[Camera] video 'suspend' at ${Date.now()} — browser stopped fetching`);
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure("[VIDEO SUSPEND — browser stopped fetching]");
    }
  };
  handleVideoPauseEvent = () => {
    console.log(`[Camera] video 'pause' at ${Date.now()}`);
    if (this.props.onCaptureFailure) {
      this.props.onCaptureFailure("[VIDEO PAUSED]");
    }
  };
  handleVideoRateChange = () => {
    console.log(`[Camera] video 'ratechange' to ${this.video?.playbackRate} at ${Date.now()}`);
  };

  /* Prevents zoom */
  disablePinchZoom = (event) => {
    event.preventDefault();
  };

  /* Beginning with iOS 10, the "user-scalable=no" attribute is no longer supported. This function will disable page zoom on-pinch for the camera page */
  pinchZoomModifier = (action) => {
    const cameraContainer = document.querySelector(".camera");

    if (action === DISABLE_ZOOM) {
      cameraContainer.addEventListener(
        "touchmove",
        this.disablePinchZoom,
        false
      );
    }

    if (action === ENABLE_ZOOM) {
      cameraContainer.removeEventListener("touchmove", this.disablePinchZoom);
    }
  };

  stopVideo = () => {
    if (this.initVideo) {
      clearTimeout(this.initVideo);
    }
  };

  stopPreview = () => {
    if (this.drawPreview) {
      clearTimeout(this.drawPreview);
    }
  };

  /**
   * Hooks the camera stream up to the video element and arms the scan loop.
   *
   * Previously this used `setTimeout(50)` then `setTimeout(500)` — totaling
   * ~550ms of artificial waiting tuned for 2018-era phones — before the
   * first frame was ever captured. On modern hardware the camera + video
   * pipeline is ready well under 200ms, so we wire the scan start to the
   * actual `playing` event and capture immediately when it fires. Saves
   * ~500-800ms off the Camera phase of TSTTM.
   */
  setupForCapturing = () => {
    this.video.srcObject = this.state.videoStream;
    this.stopVideo();
    this.captureCounter = 0;

    // Guard against double-firing if `playing` fires multiple times (e.g.,
    // after a pause/resume) — we want exactly one scan interval.
    let scanArmed = false;
    const armScanning = () => {
      if (scanArmed) return;
      scanArmed = true;
      // Fire the FIRST scan immediately (don't wait one interval tick of
      // 333ms), then 3fps after that.
      this.captureSingleScan();
      this.scan = setInterval(this.captureSingleScan, 1000 / 3);
    };

    this.video.onplaying = armScanning;
    this.playVideo();

    // Safety net: if the `playing` event doesn't fire for some reason
    // (Safari quirks, autoplay edge cases), arm scanning after a generous
    // fallback so visitors never get stuck on a frozen viewfinder.
    setTimeout(() => {
      if (
        !scanArmed &&
        this.video &&
        this.video.readyState >= 2 /* HAVE_CURRENT_DATA */
      ) {
        armScanning();
      }
    }, 1000);
  };

  componentDidUpdate(previousProps) {
    console.log(
      `previous shouldBeScanning: ${previousProps.shouldBeScanning} current shouldBeScanning: ${this.props.shouldBeScanning}, current sessionYieldedMatch: ${this.props.sessionYieldedMatch}`
    );

    if (
      !previousProps.shouldBeScanning &&
      this.props.shouldBeScanning == true
    ) {
      this.setupForCapturing();
    }

    if (this.props.shouldBeScanning == false) {
      clearInterval(this.scan);
    }
  }

  componentWillUnmount() {
    // Stop drawing the video
    this.stopVideo();
    this.stopPreview();

    // Detach mute listeners but DO NOT stop the tracks. The camera stream is
    // shared and owned by helpers/cameraStream — kept warm across the
    // scan<->artwork loop so the next scan reuses it (no cold re-acquire pause
    // or blurred-background flash). It's released at the scan-flow boundary
    // (returning Home / page unload), not on this unmount.
    if (this.state.videoStream) {
      const track = this.state.videoStream.getVideoTracks()[0];
      if (track) {
        track.removeEventListener("mute", this.handleTrackMute);
        track.removeEventListener("unmute", this.handleTrackUnmute);
      }
    }

    // Detach video element pipeline-event listeners
    if (this.video) {
      this.video.removeEventListener("waiting", this.handleVideoWaiting);
      this.video.removeEventListener("stalled", this.handleVideoStalled);
      this.video.removeEventListener("suspend", this.handleVideoSuspend);
      this.video.removeEventListener("playing", this.handleVideoPlayingEvent);
      this.video.removeEventListener("pause", this.handleVideoPauseEvent);
      this.video.removeEventListener("ratechange", this.handleVideoRateChange);
    }

    // Detach the (shared) stream from this dying <video> WITHOUT stopping it —
    // null srcObject releases this element's reference while the manager keeps
    // the stream live for the next scan.
    this.video.pause();
    this.video.srcObject = null;
    this.video.removeAttribute("src");
    this.video.load();

    // Re-enable zoom
    this.pinchZoomModifier(ENABLE_ZOOM);
  }

  /**
   * Return (lazily-created) a tempCanvas sized to the video's NATIVE source
   * dimensions. Used as an intermediate buffer so the sub-rect drawImage
   * call later operates on a CANVAS source (commodity raster path) rather
   * than a VIDEO source (which on Chromium can fall off the hardware
   * fast path for sub-rect reads — strongly suspected freeze cause).
   */
  getTempPreviewCanvas = () => {
    if (!this.tempCanvas) {
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d", { alpha: false });
      // Cap the preview buffer so the per-frame draw cost is independent of the
      // (higher-res, FOC-114) capture source — a 1920² source would make this
      // draw ~3x heavier and the reticle overlay visibly lags the native
      // <video>. The preview is shown small, so ~1080 on the long edge is ample
      // (aspect preserved). Capture (captureSingleScan) reads this.video at full
      // resolution and is unaffected.
      const vw = this.video.videoWidth || 1080;
      const vh = this.video.videoHeight || 1080;
      const previewCap = Math.min(1, 1080 / Math.max(vw, vh));
      tempCanvas.width = Math.round(vw * previewCap);
      tempCanvas.height = Math.round(vh * previewCap);
      this.tempCanvas = tempCanvas;
      this.tempCtx = tempCtx;
    }
    return { tempCanvas: this.tempCanvas, tempCtx: this.tempCtx };
  };

  /** Draws the portion of the video visible within the preview onto a canvas, in a loop. */
  drawVideoPreview = () => {
    try {
      if (!this.vpreview || !this.video || !this.video.videoWidth) {
        return null;
      }

      // STEP 1: FULL-FRAME draw video → tempCanvas (at native source
      // resolution, no transform). This is the hardware-accelerated
      // fast path on all browsers including Chromium. Old code did this.
      const { tempCanvas, tempCtx } = this.getTempPreviewCanvas();
      tempCtx.drawImage(this.video, 0, 0, tempCanvas.width, tempCanvas.height);

      // STEP 2: SUB-RECT crop draw tempCanvas → preview canvas. Source
      // is now a canvas (not the live video element). Sub-rect drawImage
      // from a canvas always uses commodity raster path — no chance of
      // falling off Chromium's video presentation pipeline.
      //
      // Crop math: viewport coords → native source coords, mirroring
      // object-fit:cover (since the video element is rendered with cover).
      const videoW = this.video.videoWidth;
      const videoH = this.video.videoHeight;
      const rect = this.video.getBoundingClientRect();
      const scale = Math.max(rect.width / videoW, rect.height / videoH);
      const offsetX = (rect.width - videoW * scale) / 2;
      const offsetY = (rect.height - videoH * scale) / 2;

      const srcX = (this.cropRectangle.x - offsetX) / scale;
      const srcY = (this.cropRectangle.y - offsetY) / scale;
      const srcW = this.cropRectangle.width / scale;
      const srcH = this.cropRectangle.height / scale;
      // tempCanvas is capped (see getTempPreviewCanvas), so scale the
      // source-pixel crop rect down to the tempCanvas' resolution before it.
      const tScale = tempCanvas.width / videoW;

      const previewCanvas = this.vpreview;
      const previewContext = previewCanvas.getContext("2d", { alpha: false });
      previewCanvas.width = this.cropRectangle.width;
      previewCanvas.height = this.cropRectangle.height;

      previewContext.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      previewContext.drawImage(
        tempCanvas,
        srcX * tScale,
        srcY * tScale,
        srcW * tScale,
        srcH * tScale,
        0,
        0,
        previewCanvas.width,
        previewCanvas.height
      );

      this.previewCanvas = previewContext.canvas;
    } catch (error) {
      // Silently swallow transient drawImage errors (video element in a
      // momentary bad state — happens during focus changes, low memory,
      // backgrounding). Critically, we still re-schedule the RAF loop in
      // the finally block below so a single bad frame doesn't permanently
      // freeze the preview pane.
      console.log("Error setting camera preview");
      // FOC-248: was GA "scanner" (read as scan volume, but it is this
      // preview-draw error). Once per page load: this catch can repeat per frame.
      trackOnce("camera_preview_error");
    } finally {
      // Stamp the last-ran time so LiveSignals can show "drawPreview Xms ago"
      // — distinguishes "our RAF loop is being throttled" (number spikes)
      // from "loop is fine but drawImage drew stale source" (number stays low).
      this.drawPreviewLastRanAt = performance.now();
      // Always re-arm the RAF loop, even after a draw error, so the
      // preview self-heals on the next frame.
      this.stopPreview();
      this.drawPreview = setTimeout(() => {
        requestAnimationFrame(this.drawVideoPreview);
      }, 10);
    }
  };

  /** Used by LiveSignals to display how long since drawVideoPreview last ran. */
  getDrawPreviewMsAgo = (): number =>
    this.drawPreviewLastRanAt
      ? Math.round(performance.now() - this.drawPreviewLastRanAt)
      : -1;

  render() {
    const { beginScanning, sessionYieldedMatch, shouldBeScanning } = this.props;

    // TESTING: blur removed entirely. If this stops the preview freezing,
    // then filter:blur on the full-viewport video was a real contributor
    // and we need a non-blur way to visually separate the scan area from
    // the surroundings. The preview reticle now has its own visible border
    // to give the eye a target without relying on background blur.
    const videoStyle: React.CSSProperties = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      objectFit: "cover",
      pointerEvents: "none",
      zIndex: 0,
    };

    return (
      <div className="camera">
        <div>
          <video
            id="video"
            ref={this.setVideoRef}
            autoPlay
            playsInline
            muted
            style={videoStyle}
          />

          {/* Show the video preview if an unsuccessful attempt has not occurred.
              z-index:1 keeps it above the blurred video at z-index:0. */}
          {sessionYieldedMatch !== false && (
            <canvas
              id="video-preview"
              ref={(el) => (this.vpreview = el)}
              style={{ zIndex: 1 }}
            />
          )}

          {/* If there was an unsuccessful attempt, transition into the no result found */}
          <NoMatchOverlay
            // @ts-ignore
            displayOverlay={
              shouldBeScanning === false && sessionYieldedMatch === false
            }
            handleScan={() => beginScanning()}
          />

          {/* Live diagnostic readout (debug builds only). Visible at top-left
              while scanning. Tells us whether a freeze is decoder-stalled
              (rVFC drops), main-thread-blocked (longtask spike), GPU/raf
              throttled (raf drops), or page-backgrounded (vis change). */}
          <LiveSignals
            videoEl={this.state.videoEl}
            videoStream={this.state.videoStream}
            getDrawPreviewMsAgo={this.getDrawPreviewMsAgo}
          />
        </div>
      </div>
    );
  }
}

export default compose(withOrientation)(CameraComponent);
